import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { SocialShareButtons } from '@/components/public/SocialShareButtons';
import type {
  ClassroomTopMomentsConfig,
  ClassroomThemeConfig,
  ClassroomQuote,
  ClassroomWishlistItem,
} from '@/types/classrooms';

const PLACEHOLDER_AVATAR = '/teacher-placeholder.svg';
const CORE_API = import.meta.env.VITE_CORE_API_URL || '';

interface ClassroomData {
  id: string;
  name: string;
  grade_level?: string | null;
  displayName: string;
  about?: string | null;
  quote?: ClassroomQuote | null;
  goals: string[];
  wishlist: ClassroomWishlistItem[];
  topMoments?: ClassroomTopMomentsConfig | null;
  theme?: ClassroomThemeConfig;
}

interface ProfileItem {
  display_name: string;
  avatar_url?: string | null;
  school?: string | null;
  district?: string | null;
  city?: string | null;
  state?: string | null;
  bio?: string | null;
  share_code?: string | null;
  classroom?: ClassroomData | null;
}

type LoadStatus = 'loading' | 'ready' | 'error';

const THEME_STYLES: Record<string, { bg: string; cardBg: string; headerBg: string; text: string }> = {
  clean: { bg: 'from-slate-50 to-blue-50/30', cardBg: 'bg-white', headerBg: 'from-blue-600 to-blue-700', text: 'text-slate-800' },
  playful: { bg: 'from-amber-50 to-rose-50/40', cardBg: 'bg-white/90', headerBg: 'from-rose-500 to-orange-500', text: 'text-slate-800' },
  bold: { bg: 'from-gray-900 to-gray-800', cardBg: 'bg-gray-800/80', headerBg: 'from-amber-500 to-amber-600', text: 'text-white' },
  minimal: { bg: 'from-gray-50 to-white', cardBg: 'bg-white', headerBg: 'from-gray-700 to-gray-800', text: 'text-gray-700' },
};

export default function ClassroomProfile() {
  const { classroomId } = useParams();
  const [item, setItem] = useState<ProfileItem | null>(null);
  const [status, setStatus] = useState<LoadStatus>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');

    const fetchProfile = async () => {
      try {
        const url = `${CORE_API}/api/v1/community/classroom-profile?id=${encodeURIComponent(classroomId || '')}`;
        const response = await fetch(url);
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data?.error || 'Classroom not found');
        if (!cancelled) { setItem(data?.item || null); setStatus('ready'); }
      } catch (error) {
        if (!cancelled) { setStatus('error'); setErrorMessage(error instanceof Error ? error.message : 'Not found'); }
      }
    };

    classroomId ? fetchProfile() : (setStatus('error'), setErrorMessage('Classroom not found'));
    return () => { cancelled = true; };
  }, [classroomId]);

  const shareUrl = useMemo(() => {
    const base = typeof window !== 'undefined' ? window.location.origin : 'https://projecttower.org';
    return `${base}/c/${classroomId}`;
  }, [classroomId]);

  const classroom = item?.classroom;
  const theme = classroom?.theme;
  const ts = THEME_STYLES[theme?.style || 'clean'] || THEME_STYLES.clean;
  const accent = theme?.accentColor || '#3b82f6';
  const isBold = theme?.style === 'bold';

  // Loading
  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50/30">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  // Error
  if (status === 'error' || !item || !classroom) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50/30 px-4">
        <div className="rounded-2xl bg-white p-8 text-center shadow-lg max-w-md">
          <h1 className="text-xl font-bold text-slate-800">Classroom Not Found</h1>
          <p className="mt-2 text-sm text-slate-500">{errorMessage}</p>
          <Link to="/" className="mt-4 inline-block text-sm text-blue-600 hover:underline">Back to Project Tower</Link>
        </div>
      </div>
    );
  }

  const avatarSrc = item.avatar_url || PLACEHOLDER_AVATAR;
  const classroomName = classroom.displayName || classroom.name || `${item.display_name}'s Classroom`;
  const locationParts = [item.school, item.district].filter(Boolean);

  return (
    <div className={`min-h-screen bg-gradient-to-br ${ts.bg}`}>
      {/* Hero Header */}
      <header className={`bg-gradient-to-r ${ts.headerBg} relative overflow-hidden`}>
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
        <div className="relative mx-auto max-w-4xl px-6 py-12 text-center text-white">
          <img
            src={avatarSrc}
            alt={item.display_name || ''}
            className="mx-auto mb-4 h-24 w-24 rounded-full border-4 border-white/30 object-cover shadow-lg"
            onError={(e) => { if (e.currentTarget.src !== PLACEHOLDER_AVATAR) e.currentTarget.src = PLACEHOLDER_AVATAR; }}
          />
          <h1 className="text-3xl font-extrabold md:text-4xl">{classroomName}</h1>
          <p className="mt-2 text-white/80">{item.display_name}</p>
          <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
            {classroom.grade_level && (
              <span className="rounded-full bg-white/20 px-3 py-1 text-xs font-medium backdrop-blur">
                {classroom.grade_level}
              </span>
            )}
            {locationParts.length > 0 && (
              <span className="rounded-full bg-white/20 px-3 py-1 text-xs font-medium backdrop-blur">
                {locationParts.join(' • ')}
              </span>
            )}
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-4 py-8 space-y-6 -mt-4">
        {/* Quote — overlapping the header */}
        {classroom.quote?.text && (
          <section className={`${ts.cardBg} rounded-2xl p-6 shadow-lg text-center`}>
            <blockquote>
              <p className={`text-xl italic leading-relaxed ${ts.text} md:text-2xl`} style={{ fontFamily: "'Georgia', serif" }}>
                &ldquo;{classroom.quote.text}&rdquo;
              </p>
              {classroom.quote.attribution && (
                <footer className="mt-3 text-sm text-slate-500">&mdash; {classroom.quote.attribution}</footer>
              )}
            </blockquote>
          </section>
        )}

        {/* About */}
        {classroom.about && (
          <section className={`${ts.cardBg} rounded-2xl p-6 shadow-md`}>
            <h2 className={`text-lg font-bold ${ts.text} mb-3`}>About Our Classroom</h2>
            <p className={`leading-relaxed ${isBold ? 'text-gray-300' : 'text-slate-600'}`}>{classroom.about}</p>
          </section>
        )}

        {/* Goals + Wishlist side by side on desktop */}
        <div className="grid gap-6 md:grid-cols-2">
          {/* Goals */}
          {classroom.goals.length > 0 && (
            <section className={`${ts.cardBg} rounded-2xl p-6 shadow-md`}>
              <h2 className={`text-lg font-bold ${ts.text} mb-4`}>Our Goals</h2>
              <div className="space-y-3">
                {classroom.goals.map((goal, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span
                      className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold text-white shadow-sm"
                      style={{ backgroundColor: accent }}
                    >
                      {i + 1}
                    </span>
                    <span className={isBold ? 'text-gray-300' : 'text-slate-700'}>{goal}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Wishlist */}
          {classroom.wishlist.length > 0 && (
            <section className={`${ts.cardBg} rounded-2xl p-6 shadow-md`}>
              <h2 className={`text-lg font-bold ${ts.text} mb-4`}>Wishlist</h2>
              <div className="space-y-2">
                {classroom.wishlist.map((w, i) => (
                  <div key={i} className={`flex items-center gap-3 rounded-xl p-3 ${isBold ? 'bg-gray-700/50' : 'bg-slate-50'}`}>
                    <span className="flex h-8 w-8 items-center justify-center rounded-full text-white" style={{ backgroundColor: accent }}>
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium truncate ${isBold ? 'text-gray-200' : 'text-slate-800'}`}>{w.label}</p>
                      {w.url && (
                        <a href={w.url} target="_blank" rel="noopener noreferrer" className="text-xs hover:underline" style={{ color: accent }}>
                          View item &rarr;
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Top Moments — visual cards with accent borders */}
        {classroom.topMoments?.items && classroom.topMoments.items.length > 0 && (
          <section className={`${ts.cardBg} rounded-2xl p-6 shadow-md`}>
            <h2 className={`text-lg font-bold ${ts.text} mb-4`}>Top Moments</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {classroom.topMoments.items
                .sort((a, b) => (a.position || 0) - (b.position || 0))
                .map((moment, idx) => (
                  <div
                    key={moment.id || idx}
                    className={`rounded-xl overflow-hidden shadow-sm border-l-4 ${isBold ? 'bg-gray-700/50' : 'bg-slate-50'}`}
                    style={{ borderLeftColor: accent }}
                  >
                    <div className="p-4">
                      <h3 className={`font-bold ${ts.text}`}>{moment.title}</h3>
                      {moment.date && <p className="mt-1 text-xs text-slate-400">{moment.date}</p>}
                      {moment.summary && <p className={`mt-2 text-sm ${isBold ? 'text-gray-300' : 'text-slate-600'}`}>{moment.summary}</p>}
                    </div>
                  </div>
                ))}
            </div>
          </section>
        )}

        {/* Support & Share */}
        <section className={`${ts.cardBg} rounded-2xl p-6 shadow-md`}>
          <div className="grid gap-6 md:grid-cols-2">
            {/* Donate CTA with share code */}
            <div className="text-center md:text-left">
              <h2 className={`text-lg font-bold ${ts.text} mb-2`}>Support This Classroom</h2>
              <p className={`text-sm mb-4 ${isBold ? 'text-gray-400' : 'text-slate-500'}`}>
                Every donation helps {item.display_name} provide resources for their students.
              </p>
              {item.share_code && (
                <Link
                  to={`/sponsor-classroom/donate?share=${item.share_code}`}
                  className="inline-block rounded-xl px-6 py-3 text-sm font-semibold text-white shadow-md transition hover:opacity-90"
                  style={{ backgroundColor: accent }}
                >
                  Donate to This Classroom
                </Link>
              )}
              {!item.share_code && (
                <Link
                  to="/sponsor-classroom/donate"
                  className="inline-block rounded-xl px-6 py-3 text-sm font-semibold text-white shadow-md transition hover:opacity-90"
                  style={{ backgroundColor: accent }}
                >
                  Donate to Classrooms
                </Link>
              )}
            </div>

            {/* Social Share */}
            <div className="text-center md:text-right">
              <h2 className={`text-lg font-bold ${ts.text} mb-2`}>Share</h2>
              <p className={`text-sm mb-4 ${isBold ? 'text-gray-400' : 'text-slate-500'}`}>
                Help spread the word!
              </p>
              <SocialShareButtons
                url={shareUrl}
                title={`Check out ${classroomName} on Project Tower!`}
                description={classroom.about || `Support ${item.display_name}'s classroom.`}
              />
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-6 text-center">
          <p className={`text-xs ${isBold ? 'text-gray-500' : 'text-slate-400'}`}>
            Powered by{' '}
            <Link to="/" className="underline hover:opacity-80">Project Tower</Link>
          </p>
        </footer>
      </div>
    </div>
  );
}
